import { useMutation, useQuery } from '@apollo/client';
import { EmailOnly } from '@apps/www/src/__generated__/graphql';
import useAuthUser from '@apps/www/src/www/hooks/useAuthUser';
import useDebouncedCallback from '@apps/www/src/www/hooks/useDebouncedCallback';
import InviteUsersToBoardMutation from '@apps/www/src/www/queries/InviteUsersToBoardMutation';
import InviteUsersToTeamMutation from '@apps/www/src/www/queries/InviteUsersToTeamMutation';
import SuggestedInvitedUsersQuery from '@apps/www/src/www/queries/SuggestedInvitedUsersQuery';
import { Transition } from '@headlessui/react';
import { SVButtonSIZES } from '@pkgs/shared-client/components/SVButton';
import SVCenter from '@pkgs/shared-client/components/SVCenter';
import SVForm from '@pkgs/shared-client/components/SVForm';
import SVMessage from '@pkgs/shared-client/components/SVMessage';
import SVModalUsersList from '@pkgs/shared-client/components/SVModalUsersList';
import { toast } from '@pkgs/shared-client/components/SVToast';
import cn from '@pkgs/shared-client/helpers/cn';
import plural from '@pkgs/shared-client/helpers/plural';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import IconSearchSVG from '@pkgs/shared-client/img/icon-search-inlined.svg';
import InviteType from '@pkgs/shared/enums/InviteType';
import { forwardRef, useEffect, useState } from 'react';

type LikeUser = Parameters<
	NonNullable<React.ComponentProps<typeof SVModalUsersList.SelectItem>['onSelect']>
>[0];

type SuggestedUserResult = {
	user:
		| {
				__typename?: 'User';
				_id: string;
				username: string;
				name: string;
				avatarURL: string;
				url: string;
				isPro: boolean;
		  }
		| {
				__typename?: 'EmailOnly';
				email: string;
		  };
	status?: {
		member: boolean;
		invited: boolean;
	} | null;
};

type Props = {
	type: typeof InviteType.TEAM | typeof InviteType.BOARD;
	resourceID?: string;
	maxUsersCount?: number;
	onUsersSelected?: (users: LikeUser[]) => void;
	newBoard?: boolean;
	className?: string;
	onSuccess?: () => void;
	onSubmit?: (submitFn: () => Promise<void>) => void;
	isJustInviteModal?: boolean;
};

function keyFromItem(item: LikeUser | EmailOnly): string {
	if ('email' in item && item.email) {
		return item.email;
	} else if ('user' in item && item.user) {
		return item.user._id;
	} else if ('_id' in item) {
		return item._id;
	}
	return '';
}

const MAX_USERS_MODAL = 6;

const _SearchSuggestionSkeleton = () => {
	return (
		<div className="flex w-full animate-pulse items-center justify-between rounded-xl py-[3px] transition-opacity duration-300">
			<div className="flex flex-row items-center">
				<div className="mr-2 h-10 w-10 rounded-full bg-gray-800"></div>
				<div className="flex flex-col space-y-2">
					<div className="h-3 w-[100px] rounded-full bg-gray-800"></div>
					<div className="h-2 w-[70px] rounded-full bg-gray-800"></div>
				</div>
			</div>
			<div className="h-9 w-[64px] rounded-xl bg-gray-800 px-4 py-2.5"></div>
		</div>
	);
};

const _UsersList = ({
	error,
	allItems,
	users,
	isLoading,
	onSelect,
	selectedUsers,
	isSearching,
	isJustInviteModal,
	type,
}: {
	error?: Error;
	allItems: SuggestedUserResult[];
	users: SuggestedUserResult[];
	isLoading: boolean;
	onSelect: ((user: LikeUser) => void) | undefined;
	selectedUsers: LikeUser[];
	isSearching: boolean;
	isJustInviteModal: boolean;
	type: typeof InviteType.TEAM | typeof InviteType.BOARD;
}) => {
	const showSkeleton = isLoading && allItems.length === 0;
	const emptyMessage = !isLoading && isSearching && allItems.length === 0 && (
		<SVCenter>
			<p>We couldn{"'"}t find anyone. Try something else.</p>
		</SVCenter>
	);

	const userMembers = users.filter((user) => user.status?.member || user.status?.invited);
	const filteredUsers = users.filter((user) => !user.status?.member && !user.status?.invited);

	return (
		<div className="flex w-full flex-col">
			{selectedUsers.length > 0 && (
				<div className="mb-4 flex-none">
					<p className="type-label mb-3 text-gray-600">Selected</p>
					<SVModalUsersList.Flex>
						{selectedUsers.map((item) => {
							const itemKey = keyFromItem(item);

							return (
								<Transition
									key={itemKey}
									appear
									show={true}
									enter="transform transition duration-300"
									enterFrom="scale-75 opacity-0"
									enterTo="scale-100 opacity-100"
								>
									<SVModalUsersList.SelectedItem
										item={item}
										onSelect={onSelect}
									/>
								</Transition>
							);
						})}
					</SVModalUsersList.Flex>
				</div>
			)}

			{isJustInviteModal && users.length > 0 && !isSearching && (
				<div className="type-label mb-3 text-gray-600">Suggested users</div>
			)}

			<div className="flex flex-col">
				{error && (
					<SVMessage scrollTo={false} use={SVMessage.USES.ERROR}>
						{error.message}
					</SVMessage>
				)}
				{showSkeleton && (
					<div className="flex flex-col space-y-3">
						{Array.from({ length: 3 }).map((_, index) => (
							<_SearchSuggestionSkeleton key={`skeleton-text-${index}`} />
						))}
					</div>
				)}
				{emptyMessage}

				<SVModalUsersList>
					{filteredUsers.map((item) => (
						<SVModalUsersList.SelectItem
							key={keyFromItem(item.user)}
							item={item.user}
							userStatus={item.status ?? undefined}
							onSelect={onSelect}
						/>
					))}
				</SVModalUsersList>

				{isJustInviteModal && userMembers.length > 0 && (
					<div className="type-label mb-3 mt-8 text-gray-600">
						In your {type === InviteType.TEAM ? 'team' : 'board'}
					</div>
				)}

				<SVModalUsersList>
					{userMembers.map((item) => (
						<SVModalUsersList.SelectItem
							key={keyFromItem(item.user)}
							item={item.user}
							userStatus={item.status ?? undefined}
							onSelect={() => {}}
						/>
					))}
				</SVModalUsersList>
			</div>
		</div>
	);
};

const SVInviteUsersContainer = forwardRef<void, Props>((props, ref) => {
	const {
		type,
		resourceID,
		maxUsersCount,
		onUsersSelected,
		newBoard,
		onSuccess,
		onSubmit,
		isJustInviteModal = false,
	} = props;

	const isInviteModal = Boolean(onSuccess);

	const [queryInput, setQueryInput] = useState('');
	const [query, setQuery] = useState('');
	const [selectedUsers, setSelectedUsers] = useState<LikeUser[]>([]);

	const trimmedQuery = query.trim();
	const isSearching = Boolean(trimmedQuery && trimmedQuery.length > 1);
	const authUser = useAuthUser(['_id']);

	const [inviteTeamUsers, { loading: mutationTeamLoading }] =
		useMutation(InviteUsersToTeamMutation);
	const [inviteBoardUsers, { loading: mutationBoardLoading }] = useMutation(
		InviteUsersToBoardMutation,
	);

	const mutationLoading = mutationTeamLoading || mutationBoardLoading;

	const {
		data: suggestedData,
		loading: suggestedLoading,
		error: suggestedError,
	} = useQuery(SuggestedInvitedUsersQuery, {
		variables: {
			type,
			resourceID: type === InviteType.BOARD && !newBoard ? resourceID : null,
			query: isSearching ? trimmedQuery : null,
		},
		fetchPolicy: 'no-cache',
	});

	const remainingUsersToAdd =
		maxUsersCount && maxUsersCount > 0 ? maxUsersCount - selectedUsers.length : null;
	const canSelectUser = remainingUsersToAdd === null || remainingUsersToAdd > 0;

	const handleQueryChange = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		if (mutationLoading) {
			return;
		}
		const value = event.target.value;
		setQueryInput(value);
		search(value);
	});

	const search = useDebouncedCallback((queryInput: string) => {
		if (mutationLoading) {
			return;
		}

		setQuery(queryInput);
	}, 300);

	const handleSelectUser = useEventCallback((user: LikeUser) => {
		if (mutationLoading) {
			return;
		}

		const selectedUser = selectedUsers.find(
			(selectedUser) =>
				('_id' in selectedUser && '_id' in user && selectedUser._id === user._id) ||
				('email' in selectedUser && 'email' in user && selectedUser.email === user.email),
		);
		const newSelectedUsers = [...selectedUsers];

		if (!selectedUser) {
			if (canSelectUser) {
				newSelectedUsers.push(user);
			}
		} else {
			newSelectedUsers.splice(selectedUsers.indexOf(selectedUser), 1);
		}

		setSelectedUsers(newSelectedUsers);
		onUsersSelected?.(newSelectedUsers);
	});

	const handleSubmit = useEventCallback(async () => {
		if (type === InviteType.BOARD && newBoard) {
			return;
		}

		const userIDs = selectedUsers
			.map((user) => !('email' in user) && '_id' in user && user._id)
			.filter(Boolean);
		const emails = selectedUsers.map((user) => 'email' in user && user.email).filter(Boolean);

		if (type === InviteType.TEAM) {
			await inviteTeamUsers({
				variables: {
					input: {
						userIDs,
						emails,
					},
				},
			});

			if (selectedUsers.length > 0) {
				const mappedUsers = selectedUsers.map((user) => {
					const isEmailUser = 'email' in user;
					return {
						name: isEmailUser
							? user.email
							: (user as any).name || (user as any).username,
					};
				});
				toast.success('Invite', {
					users: mappedUsers,
					content: 'Invite sent to',
					disableCloseButton: true,
				});
			}
		} else {
			await inviteBoardUsers({
				variables: {
					input: {
						boardID: resourceID!,
						userIDs,
						emails,
					},
				},
			});

			if (selectedUsers.length > 0) {
				toast.success('Invite', {
					users: selectedUsers.map((user) => {
						const isEmailUser = 'email' in user;
						return {
							name: isEmailUser
								? user.email
								: (user as any).name || (user as any).username,
						};
					}),
					content: 'Invite sent to',
					disableCloseButton: true,
				});
			}
		}

		if (isInviteModal && onSuccess) {
			onSuccess();
		}
	});

	useEffect(() => {
		if (onSubmit) {
			onSubmit(handleSubmit);
		}
	}, [onSubmit, handleSubmit]);

	if (!authUser) {
		return null;
	}

	const hasSelectedUsers = selectedUsers.length > 0;
	const showSubmitButton = isInviteModal && hasSelectedUsers && !newBoard;

	let itemsToShow = isInviteModal
		? suggestedData?.suggestedInvitedUsers?.slice(0, MAX_USERS_MODAL) ?? []
		: suggestedData?.suggestedInvitedUsers ?? [];

	itemsToShow = itemsToShow.filter(
		(item) =>
			!selectedUsers.some(
				(selected) =>
					('_id' in selected && '_id' in item.user && selected._id === item.user._id) ||
					('email' in selected &&
						'email' in item.user &&
						selected.email === item.user.email),
			),
	);

	return (
		<div className="flex w-full flex-col">
			{newBoard && (
				<div className="type-label text-muted mb-2.5">Collaborators (optional)</div>
			)}

			<div className={cn('relative mb-4 w-full', isJustInviteModal && 'mb-8 mt-6')}>
				<IconSearchSVG className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
				<input
					placeholder="Search by name or email"
					name="query"
					value={queryInput}
					onChange={handleQueryChange}
					className="type-base bg-background input-text-default w-full rounded-full border-0 px-3 py-2.5 pl-11 placeholder-gray-200 ring-2 ring-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-700"
					title="Add collaborators"
				/>
			</div>

			<_UsersList
				isJustInviteModal={isJustInviteModal}
				error={suggestedError}
				allItems={suggestedData?.suggestedInvitedUsers ?? []}
				users={itemsToShow}
				selectedUsers={selectedUsers}
				isLoading={suggestedLoading}
				isSearching={isSearching}
				onSelect={canSelectUser ? handleSelectUser : undefined}
				type={type}
			/>

			{showSubmitButton && (
				<SVForm
					onSubmit={handleSubmit}
					submitLabel={`Invite ${plural(selectedUsers.length, 'User')}`}
					noPadding={true}
					submitAlignment="right"
					submitClassName="type-label type-label rounded-[16px] bg-gray-200 px-6 py-5 mt-5"
					submitSize={SVButtonSIZES.TINY}
				/>
			)}
		</div>
	);
});

export default SVInviteUsersContainer;
